import React from 'react';
import { Link } from 'react-router-dom';
import './LegalPage.css';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <Link to="/" className="legal-back">&larr; Back to VizDay</Link>
        <h1>Privacy Policy</h1>
        <p className="legal-meta">Last updated: October 7, 2026</p>

        <p>
          This Privacy Policy describes how VizDay ("we", "us", "our") collects, uses, and
          protects your information when you use our service. It covers the VizDay website at
          vizday.com and the VizDay apps for Android and iOS, which share one account and one
          set of servers. VizDay is operated by VizDNA LLC.
        </p>

        <h2>1. Information We Collect</h2>
        <p><strong>Account information:</strong> name, email address, and password (or OAuth provider ID).</p>
        <p>
          <strong>Household data:</strong> a household record is created alongside your account and
          holds your own name, plus a home city and state if you provide them. You can also add
          other members of your household, together with vehicles and pets, and where you do we
          hold what you enter: names, roles, dates of birth, phone numbers, whether someone drives,
          preferences, school and work details including locations and hours, and notes about a
          vehicle or a pet&apos;s care. An earlier version of VizDay kept deeper records holding
          health and wellbeing detail — allergies and medical notes, moods, stress triggers,
          reflections and check-in answers. The interfaces that accepted them have been removed, so
          no new data of that kind can reach us. Records created while they existed remain until
          the household is deleted, and where they exist they can still inform a briefing. The screens for managing household members, vehicles, and pets were
          removed from the app during a redesign, but the interface behind them still accepts what
          is listed above. Entries can be
          deleted individually, and are removed with the household when it is deleted — though if
          another VizDay account still belongs to that household, the household and its entries
          stay with them.
        </p>
        <p>
          <strong>Calendar data:</strong> When you connect Google Calendar, Microsoft Calendar, or an ICS feed,
          we read event titles, dates, times, and locations, and from Google also attendee names. We store this data in our database
          to power scheduling features, conflict detection, briefings, and travel time calculations. We also
          store the event description and the complete event record as the provider returns it, which for
          Microsoft includes the event body. Calendar data is synced periodically, and a connected calendar
          can be switched off at any time to stop its events appearing in VizDay.
        </p>
        <p>
          VizDay&apos;s use and transfer to any other app of information received from Google APIs will
          adhere to the{' '}
          <a href="https://developers.google.com/terms/api-services-user-data-policy">
            Google API Services User Data Policy
          </a>
          , including the Limited Use requirements.
        </p>
        <p>
          <strong>Location data:</strong> If you provide a home city and state, we use them for
          weather forecasts and travel time estimates. VizDay does not read your device&apos;s
          location and does not track where you are in the background.
        </p>
        <p><strong>Usage data:</strong> pages visited, features used, and interaction patterns (via PostHog analytics).</p>
        <p>
          <strong>Payment data:</strong> VizDay is currently free and no subscription is offered for
          sale. Where a subscription exists or existed, it was purchased through the Apple App
          Store, Google Play, or Stripe on the web, and those providers process the payment. We
          store a subscription record — the provider, its identifier for the subscription, the product,
          its status, the period end, whether it renews, and selected fields from the provider's
          response. For Google Play the purchase token forms part of the identifier we store. Accounts that bought on the web also carry a Stripe customer
          identifier. We never see or store card numbers.
        </p>

        <h2>2. How We Use Your Information</h2>
        <ul>
          <li>To provide and improve the VizDay service.</li>
          <li>To generate AI-powered suggestions, briefings, and itineraries.</li>
          <li>To sync your calendars and detect scheduling conflicts.</li>
          <li>To send transactional emails (verification, password reset, briefings).</li>
          <li>To record subscription status and handle cancellations, where a subscription exists.</li>
        </ul>

        <h2>3. AI Processing</h2>
        <ul>
          <li>Your data is processed by third-party AI services to generate personalized content including briefings, chat responses, itineraries, and event parsing.</li>
          <li><strong>AI providers used:</strong> Anthropic for planning, chat, briefings, and reading dates and places out of text; Google for plan generation and chat; OpenAI for chat.</li>
          <li>We send what each feature needs rather than your whole account. For chat that is the conversation and the plan being worked on; for a briefing it is the day&apos;s events together with household context such as who is in the household and their schedules.</li>
          <li>AI providers process data under their own terms for the API tier we use. We use Google&apos;s paid Gemini API tier, whose terms state that submitted content is not used to improve their products. Anthropic and OpenAI likewise do not train on data submitted through their APIs. We hold no bespoke agreement beyond those standard terms.</li>
          <li>We do not use your personal data to train or fine-tune AI models.</li>
          <li>AI-generated content (itineraries, suggestions, briefings) is stored in your account for your reference.</li>
        </ul>

        <h2>4. Third-Party Services</h2>
        <p>We use the following third-party services:</p>
        <ul>
          <li><strong>Stripe</strong> — payment processing on the web</li>
          <li><strong>Apple</strong> — in-app purchases on iOS</li>
          <li><strong>Google</strong> — in-app purchases on Android, through Google Play Billing</li>
          <li><strong>Google</strong> — OAuth authentication and Calendar sync</li>
          <li><strong>Microsoft</strong> — OAuth authentication and Calendar sync</li>
          <li><strong>Apple</strong> — OAuth authentication</li>
          <li><strong>Anthropic</strong> — AI planning and briefings</li>
          <li><strong>OpenAI</strong> — AI chat</li>
          <li><strong>Google</strong> — AI chat</li>
          <li><strong>Tomorrow.io</strong> — weather forecasts, which receive the location a forecast is for</li>
          <li><strong>Google Maps</strong> — geocoding and travel times, which receive the places a journey runs between</li>
          <li><strong>Postmark</strong> — email delivery</li>
          <li><strong>PostHog</strong> — product analytics</li>
          <li><strong>OneSignal</strong> — push notifications where they are enabled, which receive a device identifier and the notification text</li>
          <li><strong>Sentry</strong> — error monitoring</li>
          <li><strong>DigitalOcean</strong> — hosting and infrastructure</li>
        </ul>

        <h2>5. Data Security</h2>
        <ul>
          <li>OAuth tokens are encrypted at rest using Fernet symmetric encryption.</li>
          <li>Traffic between VizDay and your browser or phone is always over HTTPS/TLS, as is traffic to our providers. The one exception is a calendar feed you add yourself: if you give us an ICS address beginning http rather than https, that feed is fetched unencrypted, because the address is yours to choose.</li>
          <li>Passwords are hashed using Django's PBKDF2 algorithm.</li>
          <li>We use managed databases with automated backups.</li>
        </ul>

        <h2>6. Data Retention</h2>
        <ul>
          <li>Account data is retained while your account is active.</li>
          <li>
            Deleting your account from the app immediately removes the account and the content
            that does not fall into the retained categories below. A deletion requested by email is
            completed within 30 days.
          </li>
          <li>
            Some records outlive the account, and several of them keep details you entered at the
            time. <Link to="/delete-account">Deleting your account</Link> lists what remains, what
            we can erase on request, and what we cannot.
          </li>
          <li>
            A plan you delete without deleting your account is erased permanently on the first
            daily clean-up after it has been deleted for 30 days.
          </li>
          <li>
            Usage statistics may be retained indefinitely. Where they are linked to you they carry
            your account number, email address, and first name, and we will erase them on request.
          </li>
          <li>
            Deleted data can persist in routine encrypted backups until those backups age out on
            their normal rotation.
          </li>
        </ul>

        <h2>7. Your Rights</h2>
        <ul>
          <li><strong>Access:</strong> Your plans, trips, and settings are visible in the app. Records the app does not display — raw calendar payloads from your provider, analytics, logs, subscription records, and photo metadata — are available on request by email.</li>
          <li><strong>Deletion:</strong> You can delete your account from Settings at any time. See <Link to="/delete-account">how to delete your account</Link> for the full process and what is removed.</li>
          <li><strong>Portability:</strong> Contact us to request an export of your data.</li>
          <li><strong>Calendars:</strong> You can switch off any connected calendar, which stops its events appearing in VizDay. Switching one off hides its events rather than erasing them — email us if you want the synced calendar data deleted.</li>
        </ul>

        <h2>8. Children{"'"}s Privacy (COPPA)</h2>
        <p>
          VizDay is designed for adults (parents and guardians) to manage their family{"'"}s schedule.
          We do not knowingly collect personal information from children under 13. Family member
          profiles (including children{"'"}s names and ages) are created and managed by the
          household{"'"}s adult account holders, who are responsible for what they enter about anyone
          in their household. Our Terms of Service set a minimum age for holding an account, and
          we do not create accounts for children under that age knowingly; we do not verify age at
          registration, so if you believe a child holds an account, tell us and we will close it.
          If you believe a child under 13 has provided personal information directly to us, please
          contact us at <a href="mailto:arnold@vizday.com">arnold@vizday.com</a> and we will promptly delete it.
        </p>

        <h2>9. California Privacy Rights (CCPA)</h2>
        <p>If you are a California resident, you have the following rights under the California Consumer Privacy Act:</p>
        <ul>
          <li><strong>Right to Know:</strong> You may request the categories and specific pieces of personal information we have collected about you.</li>
          <li><strong>Right to Delete:</strong> You may request deletion of your personal information by us and by the providers who process it for us, subject to the exceptions the law allows. The "Delete Account" option in Settings removes the account and most of its data but does not reach everything — <Link to="/delete-account">what deletion removes and what it leaves</Link> explains the difference, and email us to exercise the right in full.</li>
          <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your privacy rights.</li>
          <li><strong>Do Not Sell:</strong> We do not sell your personal information to third parties. We do not share personal information for cross-context behavioral advertising.</li>
        </ul>
        <p>
          To exercise any of these rights, email{' '}
          <a href="mailto:arnold@vizday.com">arnold@vizday.com</a>. The in-app Delete Account
          control exercises part of the deletion right immediately, but it cannot submit a
          request to know, to access, or to correct — those come to us by email.
        </p>

        <h2>10. European Privacy Rights (GDPR)</h2>
        <p>If you are in the European Economic Area (EEA) or United Kingdom, you have additional rights:</p>
        <ul>
          <li><strong>Legal basis:</strong> We process your data based on your consent (calendar access), contractual necessity (providing the Service), and legitimate interest (security, analytics).</li>
          <li><strong>Right to Access:</strong> Request a copy of your personal data.</li>
          <li><strong>Right to Rectification:</strong> Email us to correct inaccurate personal data. Settings shows your name and email address but does not currently allow editing them.</li>
          <li><strong>Right to Erasure:</strong> Request deletion of your personal data.</li>
          <li><strong>Right to Portability:</strong> Request your data in a structured, machine-readable format.</li>
          <li><strong>Right to Object:</strong> Object to processing based on legitimate interest.</li>
          <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time — switch off a connected calendar to stop VizDay using it, and email us to have the data it synced deleted.</li>
        </ul>
        <p>
          Data is stored on servers in the United States (DigitalOcean), and the providers listed in
          section 4 process it there and elsewhere. Transfers out of the EEA and UK are made on the
          basis that they are necessary to provide the service you have asked us for, and we rely on
          the safeguards our providers offer for international transfers rather than on your consent
          alone. If you want to know what applies to a particular provider, email us and we will
          tell you.
        </p>

        <h2>11. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. We will notify you of material
          changes via email or in-app notification.
        </p>

        <h2>12. Contact</h2>
        <p>
          Questions about this Privacy Policy? Contact us at{' '}
          <a href="mailto:arnold@vizday.com">arnold@vizday.com</a>.
        </p>

        <div className="legal-links">
          <Link to="/terms">Terms of Service</Link>
          <Link to="/delete-account">Delete your account</Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
